import { useTranslation } from "react-i18next";

import { companyNames } from "./model";
import type { ContactMatch } from "./queries";
import type { Capture } from "./rows";
import { AssignButton, OwnerTag, PersonLine } from "./rows";

/**
 * People the search found — by their name, their number or their address.
 *
 * One component for all three sections because the row is the same in each: a
 * person, the company they answer for — under both its names, as the caller may
 * know it by either — and the button that hands them the call. What differs is only why they are on the list, and the section's own
 * heading says that.
 */
export function ContactMatches({
  contacts,
  capture,
}: {
  contacts: readonly ContactMatch[];
  capture: Capture;
}) {
  const { t } = useTranslation();
  return (
    <ul className="space-y-2">
      {contacts.map((contact) => (
        <li
          key={contact.id}
          className="flex flex-wrap items-center justify-between gap-3 border-2 border-rule bg-panel p-4"
        >
          <div className="min-w-0">
            <PersonLine
              name={contact.name}
              jobTitle={contact.job_title}
              phones={contact.phones}
              emails={contact.emails}
              disabled={contact.is_disabled}
            />
            <p className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
              <span className="text-mute-600">{t("reception.worksAt")}</span>
              <span className="font-medium">{companyNames(contact.company)}</span>
              <OwnerTag company={contact.company} />
            </p>
          </div>
          {contact.is_disabled ? null : (
            <AssignButton
              capture={capture}
              company={contact.company}
              contact={contact}
            />
          )}
        </li>
      ))}
    </ul>
  );
}
